/**
 * LegalDocsAcceptance — Booking checkout legal document acceptance panel
 * Fetches required_for=booking docs from Supabase, shows checkboxes + expandable content.
 */

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getSupabaseClient } from "../../../lib/supabaseClient";
import type { LegalDocument, LegalI18nLang } from "../../../store/types";

interface Props {
  /** Called whenever checked state changes. allMandatoryAccepted=true means form can submit */
  onChange: (acceptedIds: string[], allMandatoryAccepted: boolean) => void;
}

type Lang = "zh-TW" | "en-US" | "ja-JP";

function toLang(i18nLang: string): Lang {
  if (i18nLang.startsWith("zh")) return "zh-TW";
  if (i18nLang.startsWith("ja")) return "ja-JP";
  return "en-US";
}

export default function LegalDocsAcceptance({ onChange }: Props) {
  const { t, i18n } = useTranslation();
  const lang: Lang = toLang(i18n.language ?? "en");

  const [docs, setDocs] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Fallback copy for i18n keys that may not exist yet
  const copy = {
    loading: t("tabifoto.booking.legal_loading", "正在載入條款..."),
    agree_prefix: t("tabifoto.booking.legal_agree_prefix", "我同意"),
    version_prefix: t("tabifoto.booking.legal_version_prefix", "版本"),
    effective_prefix: t("tabifoto.booking.legal_effective_prefix", "生效日期"),
    view_content: t("tabifoto.booking.legal_view_content", "閱讀條款"),
    hide_content: t("tabifoto.booking.legal_hide_content", "收起"),
    required_badge: t("tabifoto.booking.legal_required", "必填"),
    optional_badge: t("tabifoto.booking.legal_optional", "選填"),
    section_title: t("tabifoto.booking.legal_section_title", "服務條款確認"),
    section_sub: t("tabifoto.booking.legal_section_sub", "請閱讀並確認以下條款後繼續"),
    all_required_msg: t("tabifoto.booking.legal_all_required_msg", "請勾選所有必填條款"),
  };

  useEffect(() => {
    async function fetchDocs() {
      try {
        const supabase = getSupabaseClient();
        const { data, error } = await supabase
          .from("legal_documents")
          .select("*")
          .contains("required_for", ["booking"])
          .eq("is_active", true)
          .order("display_order", { ascending: true });

        if (error) throw error;
        setDocs((data as LegalDocument[]) ?? []);
      } catch {
        // If Supabase fails or no docs configured — show nothing, don't block
        setDocs([]);
      } finally {
        setLoading(false);
      }
    }
    void fetchDocs();
  }, []);

  // Compute allMandatoryAccepted whenever acceptedIds or docs changes
  useEffect(() => {
    const mandatoryIds = docs.filter((d) => d.is_mandatory).map((d) => d.id);
    const allMandatoryAccepted = mandatoryIds.every((id) => acceptedIds.has(id));
    onChange(Array.from(acceptedIds), allMandatoryAccepted);
  }, [acceptedIds, docs, onChange]);

  const toggleAccept = (id: string) => {
    setAcceptedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#2C2C2C]/30 py-2">
        <div className="w-3 h-3 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-sm" />
        </div>
        {copy.loading}
      </div>
    );
  }

  if (docs.length === 0) return null;

  const mandatoryIds = docs.filter((d) => d.is_mandatory).map((d) => d.id);
  const allMandatoryAccepted = mandatoryIds.every((id) => acceptedIds.has(id));

  return (
    <div className="border border-[#D9D9D9] rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 bg-[#F7F6F3] border-b border-[#D9D9D9]/60">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center text-[#2C2C2C]/60">
            <i className="ri-shield-check-line text-sm" />
          </div>
          <p className="text-xs font-light text-[#2C2C2C] uppercase tracking-widest">{copy.section_title}</p>
        </div>
        <p className="text-xs font-light text-[#2C2C2C]/40 mt-0.5 ml-6">{copy.section_sub}</p>
      </div>

      {/* Doc list */}
      <div className="divide-y divide-[#D9D9D9]/30">
        {docs.map((doc) => {
          const title = (doc.title_i18n as Record<Lang, string>)[lang]
            || doc.title_i18n["zh-TW" as LegalI18nLang]
            || doc.title_i18n["en-US" as LegalI18nLang]
            || "—";
          const content = (doc.content_i18n as Record<Lang, string>)[lang]
            || doc.content_i18n["zh-TW" as LegalI18nLang]
            || "";
          const isChecked = acceptedIds.has(doc.id);
          const isExpanded = expandedId === doc.id;

          return (
            <div key={doc.id} className="px-5 py-4">
              {/* Checkbox row */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5 shrink-0">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleAccept(doc.id)}
                    className="w-4 h-4 accent-[#2C2C2C] cursor-pointer"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-light text-[#2C2C2C] group-hover:text-[#2C2C2C]/80 transition-colors">
                      {copy.agree_prefix} {title}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-light ${
                      doc.is_mandatory
                        ? "bg-rose-50 text-rose-600"
                        : "bg-[#F7F6F3] text-[#2C2C2C]/40"
                    }`}>
                      {doc.is_mandatory ? copy.required_badge : copy.optional_badge}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-[#2C2C2C]/30">
                    <span>{copy.version_prefix} {doc.version}</span>
                    {doc.effective_date && (
                      <span>{copy.effective_prefix} {doc.effective_date}</span>
                    )}
                    {content && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setExpandedId(isExpanded ? null : doc.id); }}
                        className="text-[#C8B8A6] hover:text-[#B0A090] transition-colors cursor-pointer underline-offset-2 hover:underline whitespace-nowrap"
                      >
                        {isExpanded ? copy.hide_content : copy.view_content}
                      </button>
                    )}
                  </div>
                </div>
              </label>

              {/* Expandable content */}
              {isExpanded && content && (
                <div className="ml-7 mt-3 px-4 py-3 bg-[#F7F6F3] rounded-lg border border-[#D9D9D9]/40">
                  <p className="text-xs font-light text-[#2C2C2C]/60 leading-relaxed whitespace-pre-line">
                    {content}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Validation hint */}
      {mandatoryIds.length > 0 && !allMandatoryAccepted && (
        <div className="px-5 py-2.5 bg-amber-50 border-t border-amber-100 flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center text-amber-500 shrink-0">
            <i className="ri-information-line text-sm" />
          </div>
          <p className="text-xs font-light text-amber-700">{copy.all_required_msg}</p>
        </div>
      )}
    </div>
  );
}